import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapPin, ExternalLink, MapPinOff } from 'lucide-react';
import { isUsableCoordinate } from './geo';
import { buildTileLayout, osmTileUrl, TILE_SIZE } from './tileMath';

interface StaticMapProps {
  lat: number | null | undefined;
  lng: number | null | undefined;
  /** A térkép alatt megjelenő felirat — jellemzően a cím. */
  label?: string | null;
  zoom?: number;
  /** A térkép magassága pixelben. A szélesség a szülőhöz igazodik. */
  height?: number;
  className?: string;
  /** Ha `false`, nem jelenik meg a "Megnyitás" hivatkozás. */
  showLink?: boolean;
}

/**
 * Statikus térkép egy koordinátához.
 *
 * ── Miért kép, és nem beágyazott, interaktív térkép ───────────────────────
 * A cím- és GPS-mezők mellett a kérdés mindig ugyanaz: "tényleg ott van?".
 * Ehhez egy pillantás kell, nem navigáció. Egy interaktív térkép behúzná a
 * teljes Maps JS API-t minden űrlapon, elnyelné a görgetést a mezők között,
 * és mobilon véletlen mozgatásra csábítana. Itt nincs se görgetés, se
 * nagyítás — aki nézelődne, egy kattintással megnyitja a Mapsben.
 *
 * ── Miért OpenStreetMap-csempék ───────────────────────────────────────────
 * A Google Static Maps külön API, amit a projekt kulcsán engedélyezni és
 * számlázni kell; enélkül 403-mal válaszol. A csempék ellenben kulcs nélkül
 * elérhetők, és ez a nézet úgyis csak néhány képet kér oldalanként. Az
 * attribúció (OSM licencfeltétel) a térkép sarkában látszik.
 *
 * ── Hibatűrés ─────────────────────────────────────────────────────────────
 * Ha a csempék nem töltődnek be, a komponens NEM tűnik el: a koordinátákat és
 * a Maps-hivatkozást változatlanul megmutatja. A térkép kiegészítő
 * információ, nem az egyetlen forrás.
 */
export const StaticMap: React.FC<StaticMapProps> = ({
  lat, lng, label, zoom = 14, height = 200, className = '', showLink = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [failedTiles, setFailedTiles] = useState(0);

  const hasCoords = isUsableCoordinate(lat, lng);

  // A csempekiosztáshoz a nézet TÉNYLEGES pixelszélessége kell — az arány
  // önmagában nem elég, mert a csempék fix 256 pixelesek, és rossz szélességgel
  // a szélek üresen maradnának.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasCoords]);

  // Új koordinátánál újra megpróbáljuk — enélkül egyetlen hibás betöltés után
  // a térkép a munkamenet végéig szöveges tartalékon maradna.
  useEffect(() => { setFailedTiles(0); }, [lat, lng, zoom]);

  const layout = useMemo(() => {
    if (!hasCoords || width <= 0) return null;
    return buildTileLayout(Number(lat), Number(lng), zoom, width, height);
  }, [hasCoords, lat, lng, zoom, width, height]);

  if (!hasCoords) {
    return (
      <div
        className={`rounded-lg border border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1 text-gray-400 ${className}`}
        style={{ height }}
      >
        <MapPinOff className="w-5 h-5" />
        <span className="text-xs">Adj meg címet vagy koordinátát a térképhez.</span>
      </div>
    );
  }

  const coords = `${lat},${lng}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coords)}`;
  // Ha minden csempe elbukott, tartalékra váltunk. Egy-egy hiányzó csempe
  // (pl. lassú hálózat) még nem indok rá.
  const allTilesFailed = layout !== null && failedTiles >= layout.tiles.length;

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
        style={{ height }}
      >
        {layout && !allTilesFailed ? (
          <>
            {layout.tiles.map(tile => (
              <img
                key={`${tile.z}/${tile.x}/${tile.y}`}
                src={osmTileUrl(tile.x, tile.y, tile.z)}
                alt=""
                aria-hidden="true"
                loading="lazy"
                draggable={false}
                onError={() => setFailedTiles(n => n + 1)}
                className="absolute select-none max-w-none"
                style={{ left: tile.left, top: tile.top, width: TILE_SIZE, height: TILE_SIZE }}
              />
            ))}

            {/* A jelölő a nézet közepén áll; a csúcsa mutat a pontra, ezért
                a vízszintes közép mellett a MAGASSÁGÁVAL is el kell tolni. */}
            <MapPin
              className="absolute w-7 h-7 text-teal-700 drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)] pointer-events-none"
              fill="#0d9488"
              style={{ left: layout.markerLeft - 14, top: layout.markerTop - 28 }}
              aria-hidden="true"
            />

            {/* Az OSM licence megköveteli a forrás feltüntetését. */}
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-0 right-0 bg-white/80 text-[9px] text-gray-600 px-1 leading-4 hover:underline"
            >
              © OpenStreetMap
            </a>

            <span className="sr-only">
              Térkép: {label || coords}
            </span>
          </>
        ) : (
          // Tartalék: a koordináta önmagában is használható információ.
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-gray-500 bg-gray-50">
            <MapPin className="w-6 h-6 text-teal-600" />
            <span className="text-sm font-mono">{Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}</span>
            <span className="text-[11px] text-gray-400">A térkép előnézete nem érhető el.</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-1.5">
        {label ? (
          <p className="text-xs text-gray-500 truncate" title={label}>{label}</p>
        ) : (
          <p className="text-xs text-gray-400 font-mono">{Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}</p>
        )}
        {showLink && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-medium text-teal-700 hover:underline inline-flex items-center gap-1 flex-shrink-0"
          >
            Megnyitás <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
};
